import {
  AccountHolderMaritalStatusDto,
  AccountHolderAddressDto,
  AccountHolderDisclosureDto,
  AccountHolderEmploymentStatusDto,
  AccountHolderFinancialInformationDto,
  AccountHolderStatementDeliveryDto,
  AccountHolderTrustedContactDto,
  AccountHolderYearsEmployedDto,
  PhoneNumberDto,
} from 'src/dtos';
import { getCountryOptionLabel, getUnitedStateOptionLabel, Maybe } from 'src/utils';

export class AccountHolderAddress {
  private _value: Omit<AccountHolderAddressDto, 'state' | 'country'> & {
    state: { value: string; label: string };
    country: { value: string; label: string };
    sameAsPhysical?: boolean;
  };

  constructor(dto: AccountHolderAddressDto & { isSameASPhysical?: boolean }) {
    this._value = {
      ...dto,
      state: { value: dto.state, label: getUnitedStateOptionLabel(dto.state) ?? 'NA' },
      country: { value: dto.country, label: getCountryOptionLabel(dto.country) ?? 'NA' },
    };
  }

  get address1(): string {
    return this._value.address1;
  }

  get address2(): Maybe<string> {
    return this._value.address2;
  }

  get city(): string {
    return this._value.city;
  }

  get state(): { value: string; label: string } {
    return this._value.state;
  }

  get country(): { value: string; label: string } {
    return this._value.country;
  }

  get postalCode(): string {
    return this._value.postalCode;
  }

  get sameAsPhysical(): Maybe<boolean> {
    return this._value.sameAsPhysical;
  }
}

export enum AccountHolderMaritalStatusLabel {
  Single = 'Single',
  Married = 'Married',
  Divorced = 'Divorced',
  Widowed = 'Widowed',
}

export class AccountHolderMaritalStatus {
  private _label?: AccountHolderMaritalStatusLabel;
  private _value?: AccountHolderMaritalStatusDto;

  constructor(status?: AccountHolderMaritalStatusDto) {
    this._value = status;
    this._label = this.findLabel();
  }

  private findLabel(): Maybe<AccountHolderMaritalStatusLabel> {
    switch (this._value) {
      case AccountHolderMaritalStatusDto.Single:
        return AccountHolderMaritalStatusLabel.Single;
      case AccountHolderMaritalStatusDto.Married:
        return AccountHolderMaritalStatusLabel.Married;
      case AccountHolderMaritalStatusDto.Divorced:
        return AccountHolderMaritalStatusLabel.Divorced;
      case AccountHolderMaritalStatusDto.Widowed:
        return AccountHolderMaritalStatusLabel.Widowed;
      default:
        return undefined;
    }
  }

  get value(): Maybe<AccountHolderMaritalStatusDto> {
    return this._value;
  }

  get label(): Maybe<AccountHolderMaritalStatusLabel> {
    return this._label;
  }
}

export class AccountHolderTrustedContact {
  private _value: Omit<
    AccountHolderTrustedContactDto,
    'address1' | 'address2' | 'city' | 'state' | 'country' | 'postalCode'
  > & {
    address: AccountHolderAddress;
  };

  constructor(dto: AccountHolderTrustedContactDto) {
    this._value = {
      ...dto,
      address: new AccountHolderAddress({
        address1: dto.address1,
        address2: dto.address2,
        city: dto.city,
        state: dto.state,
        country: dto.country,
        postalCode: dto.postalCode,
      }),
    };
  }

  get firstName(): Maybe<string> {
    return this._value.firstName;
  }

  get lastName(): Maybe<string> {
    return this._value.lastName;
  }

  get email(): Maybe<string> {
    return this._value.email;
  }

  get phones(): Maybe<PhoneNumberDto[]> {
    return this._value.phones;
  }

  get address1(): string {
    return this._value.address.address1;
  }

  get address2(): Maybe<string> {
    return this._value.address.address2;
  }

  get city(): string {
    return this._value.address.city;
  }

  get state(): { value: string; label: string } {
    return this._value.address.state;
  }

  get country(): { value: string; label: string } {
    return this._value.address.country;
  }

  get postalCode(): string {
    return this._value.address.postalCode;
  }
}

export enum AccountHolderEmploymentStatusLabel {
  Employed = 'Employed',
  Retired = 'Retired',
  Student = 'Student',
  Unemployed = 'Unemployed',
}

export enum AccountHolderYearsEmployedLabel {
  BetweenZeroAndOne = 'Less than 1 year',
  BetweenTwoAndFive = '2 to 5 years',
  BetweenFiveAndTen = '5 to 10 years',
  BetweenTenAndTwenty = '10 to 20 years',
  MoreThanTwenty = 'More than 20 years',
}
export class AccountHolderFinancialInformation {
  private _employmentStatusValue: Maybe<AccountHolderEmploymentStatusDto>;
  private _employmentStatusLabel: Maybe<AccountHolderEmploymentStatusLabel>;
  private _employerName: Maybe<string>;
  private _jobTitle: Maybe<string>;
  private _yearsEmployedValue: Maybe<AccountHolderYearsEmployedDto>;
  private _yearsEmployedLabel: Maybe<AccountHolderYearsEmployedLabel>;
  private _employerAddress: Maybe<AccountHolderAddress>;

  constructor(dto: AccountHolderFinancialInformationDto) {
    this._employmentStatusValue = dto.employmentStatus;
    this._employmentStatusLabel = dto.employmentStatus
      ? this.findEmploymentStatusLabel(dto.employmentStatus)
      : undefined;
    this._employerName = dto.employerName;
    this._jobTitle = dto.jobTitle;
    this._yearsEmployedValue = dto.yearsEmployed;
    this._yearsEmployedLabel = dto.yearsEmployed ? this.findYearsEmployedLabel(dto.yearsEmployed) : undefined;
    this._employerAddress = dto.employerAddress ? this.findEmployerAddress(dto.employerAddress) : undefined;
  }

  private findEmploymentStatusLabel(
    employmentStatus: AccountHolderEmploymentStatusDto,
  ): AccountHolderEmploymentStatusLabel {
    switch (employmentStatus) {
      case AccountHolderEmploymentStatusDto.Employed:
        return AccountHolderEmploymentStatusLabel.Employed;
      case AccountHolderEmploymentStatusDto.Retired:
        return AccountHolderEmploymentStatusLabel.Retired;
      case AccountHolderEmploymentStatusDto.Student:
        return AccountHolderEmploymentStatusLabel.Student;
      case AccountHolderEmploymentStatusDto.Unemployed:
        return AccountHolderEmploymentStatusLabel.Unemployed;
    }
  }

  private findYearsEmployedLabel(yearsEmployed: AccountHolderYearsEmployedDto): AccountHolderYearsEmployedLabel {
    switch (yearsEmployed) {
      case AccountHolderYearsEmployedDto.BetweenZeroAndOne:
        return AccountHolderYearsEmployedLabel.BetweenZeroAndOne;
      case AccountHolderYearsEmployedDto.BetweenTwoAndFive:
        return AccountHolderYearsEmployedLabel.BetweenTwoAndFive;
      case AccountHolderYearsEmployedDto.BetweenFiveAndTen:
        return AccountHolderYearsEmployedLabel.BetweenFiveAndTen;
      case AccountHolderYearsEmployedDto.BetweenTenAndTwenty:
        return AccountHolderYearsEmployedLabel.BetweenTenAndTwenty;
      case AccountHolderYearsEmployedDto.MoreThanTwenty:
        return AccountHolderYearsEmployedLabel.MoreThanTwenty;
    }
  }

  private findEmployerAddress(address: AccountHolderAddressDto): AccountHolderAddress {
    return new AccountHolderAddress(address);
  }

  get employmentStatus(): Maybe<{
    value: AccountHolderEmploymentStatusDto;
    label: AccountHolderEmploymentStatusLabel;
  }> {
    return this._employmentStatusValue && this._employmentStatusLabel
      ? { value: this._employmentStatusValue, label: this._employmentStatusLabel }
      : undefined;
  }

  get employerName(): Maybe<string> {
    return this._employerName;
  }

  get jobTitle(): Maybe<string> {
    return this._jobTitle;
  }

  get yearsEmployed(): Maybe<{ value: AccountHolderYearsEmployedDto; label: AccountHolderYearsEmployedLabel }> {
    return this._yearsEmployedValue && this._yearsEmployedLabel
      ? { value: this._yearsEmployedValue, label: this._yearsEmployedLabel }
      : undefined;
  }

  get employerAddress(): Maybe<AccountHolderAddress> {
    return this._employerAddress;
  }
}

export enum AccountHolderStatementDeliveryLabel {
  Electronic = 'Electronic (Free)',
  Paper = 'Paper (Fees apply)',
}

export class AccountHolderDisclosures {
  private _value: Omit<AccountHolderDisclosureDto, 'statementDelivery'> & {
    statementDelivery: { value: AccountHolderStatementDeliveryDto; label: AccountHolderStatementDeliveryLabel };
  };

  constructor(dto: AccountHolderDisclosureDto) {
    this._value = {
      ...dto,
      statementDelivery: {
        value: dto.statementDelivery,
        label:
          dto.statementDelivery === AccountHolderStatementDeliveryDto.Electronic
            ? AccountHolderStatementDeliveryLabel.Electronic
            : AccountHolderStatementDeliveryLabel.Paper,
      },
    };
  }

  get statementDelivery(): { value: AccountHolderStatementDeliveryDto; label: AccountHolderStatementDeliveryLabel } {
    return this._value.statementDelivery;
  }

  get stakeholder(): false | { tickerSymbol: string } {
    return this._value.stakeholder;
  }

  get industryEmployed(): false | { firmName: string } {
    return this._value.industryEmployed;
  }
}

export interface AccountHolder {
  id: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
  dateOfBirth?: string;
  phones?: PhoneNumberDto[];
  email?: string;
  ssid?: string;
  maskedSsid?: string;
  maritalStatus?: AccountHolderMaritalStatus;
  numberOfDependents?: number;
  birthCountry?: { label: string; value: string };
  citizenshipCountry?: { label: string; value: string };
  physicalAddress?: AccountHolderAddress;
  mailingAddress?: AccountHolderAddress;
  trustedContact?: AccountHolderTrustedContact;
  financialInformation?: AccountHolderFinancialInformation;
  disclosure?: AccountHolderDisclosures;
  jointTenantsInCommonInterestPercent?: number;
}
